import { lazy, Suspense } from 'react';
import { PermissionGuard, PermissionSwitch } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { PERMISSIONS } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { NewsPostListSearchParamsSchema } from 'src/sections/news/news-post-list/search-params';

import { rootRoute } from './root';

const NewsPostFeed = lazy(() => import('src/sections/news/news-post-feed/view'));
const NewsPostList = lazy(() => import('src/sections/news/news-post-list/view'));
const AddNewsPost = lazy(() => import('src/sections/news/add-news-post/view'));
const EditNewsPost = lazy(() => import('src/sections/news/edit-news-post/view'));
const NewsPostDetails = lazy(() => import('src/sections/news/news-post-details/view'));
const NewsPostAdminView = lazy(() => import('src/sections/news/news-post-details/admin-view'));

// News routes
const newsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'news',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard requiredPermissions={[PERMISSIONS.NEWS_READ]}>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const newsIndexRoute = createRoute({
  getParentRoute: () => newsRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    NewsPostListSearchParamsSchema.validateSync(search),
  component: () => (
    <PermissionSwitch
      options={[
        { permissions: [PERMISSIONS.NEWS_WRITE], component: <NewsPostList /> },
        { permissions: [PERMISSIONS.NEWS_READ], component: <NewsPostFeed /> },
      ]}
    />
  ),
});

const addNewsPostRoute = createRoute({
  getParentRoute: () => newsRoute,
  path: 'add',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.NEWS_WRITE]}>
      <AddNewsPost />
    </PermissionGuard>
  ),
});

const newsPostDetails = createRoute({
  getParentRoute: () => newsRoute,
  path: '$newsPostId',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.NEWS_READ]}>
      <NewsPostDetails />
    </PermissionGuard>
  ),
});

const newsPostAdminView = createRoute({
  getParentRoute: () => newsRoute,
  path: '$newsPostId/view',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.NEWS_WRITE]}>
      <NewsPostAdminView />
    </PermissionGuard>
  ),
});

const editNewsPostRoute = createRoute({
  getParentRoute: () => newsRoute,
  path: '$newsPostId/edit',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.NEWS_WRITE]}>
      <EditNewsPost />
    </PermissionGuard>
  ),
});

export const newsRouteTree = newsRoute.addChildren([
  newsIndexRoute,
  addNewsPostRoute,
  newsPostDetails,
  newsPostAdminView,
  editNewsPostRoute,
]);
